import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, FileText, Building2, Building, Users, BarChart3, Settings2,
  Network, Calendar, Banknote, Hash, UserCog, ChevronDown,
  ArrowUpFromLine, ArrowDownToLine, HardHat, Calculator, HandCoins, PackageCheck,
  CreditCard, Flag, CalendarClock, CalendarCheck, Landmark, Warehouse,
  ShoppingCart, UserSquare2, Package, FileEdit, FileInput, ClipboardCheck, ShoppingBag, Truck, Printer,
  Brain, KeyRound, ArrowLeftRight, ShieldCheck, PlugZap, PackageX, ScanLine, RefreshCw,
  PackageSearch, Home, Clock, ClipboardList, CircleDollarSign, FolderTree, Tag, Layers, AlertOctagon, Percent,
  ChevronLeft, ChevronRight, Download, Trash2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { SidaWordmark } from "@/components/brand/SidaWordmark";
import { SidaMark } from "@/components/brand/SidaMark";
import { useDirtyNav } from "@/contexts/DirtyNavContext";
import { tr } from "@/lib/tr";

import { getPageKeyForPath } from "@/lib/pagePermissionMap";
import { isHiddenRoute } from "@/lib/hiddenRoutes";
import { usePermissions } from "@/hooks/usePermissions";

interface NavItem { to: string; labelKey: string; icon: any; soon?: boolean; }
interface NavGroup { labelKey: string; icon: any; items: NavItem[]; }
type Divider = { divider: true };
type Entry = NavItem | NavGroup | Divider;

const groups: Entry[] = [
  { to: "/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard } as NavItem,
  { to: "/ai", labelKey: "nav.aiAdvisor", icon: Brain } as NavItem,
  { divider: true } as Divider,
  {
    labelKey: "nav.financialOps", icon: Banknote, items: [
      { to: "/journal", labelKey: "nav.journal", icon: FileText },
      { to: "/vouchers/payment", labelKey: "nav.voucherPayment", icon: ArrowUpFromLine },
      { to: "/vouchers/receipt", labelKey: "nav.voucherReceipt", icon: ArrowDownToLine },
      { to: "/cash-count", labelKey: "nav.cashCount", icon: Calculator },
    ]
  } as NavGroup,
  {
    labelKey: "nav.sales", icon: ShoppingCart, items: [
      { to: "/sales/invoices", labelKey: "nav.salesInvoices", icon: FileText },
      { to: "/sales/orders", labelKey: "nav.salesOrders", icon: FileEdit },
      { to: "/sales/customers", labelKey: "nav.customers", icon: UserSquare2 },
    ]
  } as NavGroup,
  {
    labelKey: "nav.purchases", icon: ShoppingBag, items: [
      { to: "/purchases/invoices", labelKey: "nav.purchaseInvoices", icon: FileInput },
      { to: "/purchases/orders", labelKey: "nav.purchaseOrders", icon: ClipboardCheck },
      { to: "/purchases/vendors", labelKey: "nav.vendors", icon: Truck },
    ]
  } as NavGroup,
  {
    labelKey: "nav.inventory", icon: Warehouse, items: [
      { to: "/inventory/transfers", labelKey: "nav.inventoryTransfers", icon: ArrowLeftRight },
      { to: "/inventory/damages", labelKey: "nav.damages", icon: PackageX },
      { to: "/inventory/stock-take", labelKey: "nav.stockTake", icon: ScanLine },
      { to: "/inventory/pending-revaluation", labelKey: "nav.pendingRevaluation", icon: RefreshCw },
      { to: "/inventory/period-close", labelKey: "nav.inventoryPeriodClose", icon: CalendarCheck },
      { to: "/inventory/integrity", labelKey: "nav.inventoryIntegrity", icon: PackageSearch },
    ]
  } as NavGroup,
  {
    labelKey: "nav.assetsAndContracts", icon: Landmark, items: [
      { to: "/assets", labelKey: "nav.assets", icon: Building2 },
      { to: "/leases", labelKey: "nav.leases", icon: Home },
      { to: "/prepaid", labelKey: "nav.prepaid", icon: Clock },
      { to: "/cip", labelKey: "nav.cip", icon: HardHat },
      { to: "/asset-custody-transfer", labelKey: "nav.assetCustodyTransfer", icon: PackageCheck },
      { to: "/cash-custody", labelKey: "nav.cashCustody", icon: HandCoins },
    ]
  } as NavGroup,
  {
    labelKey: "nav.accrualsDeferrals", icon: CalendarClock, items: [
      { to: "/accruals", labelKey: "nav.accruals", icon: CircleDollarSign },
    ]
  } as NavGroup,
  { to: "/closing-review", labelKey: "nav.closingReview", icon: ClipboardList } as NavItem,
  { to: "/reports", labelKey: "nav.reports", icon: BarChart3 } as NavItem,
  { divider: true } as Divider,
  {
    labelKey: "nav.setup", icon: Settings2, items: [
      { to: "/setup/accounts", labelKey: "nav.chartOfAccounts", icon: Network },
      { to: "/sales/products", labelKey: "nav.products", icon: Package },
      { to: "/setup/product-categories", labelKey: "nav.productCategories", icon: FolderTree },
      { to: "/setup/cost-centers", labelKey: "nav.costCenters", icon: Tag },
      { to: "/setup/asset-groups", labelKey: "nav.assetGroups", icon: Layers },
      { to: "/setup/warehouses", labelKey: "nav.warehouses", icon: Warehouse },
      { to: "/setup/damage-reasons", labelKey: "nav.damageReasons", icon: AlertOctagon },
      { to: "/setup/fiscal-periods", labelKey: "nav.fiscalPeriods", icon: Calendar },
      { to: "/setup/taxes-zakat", labelKey: "nav.taxesZakat", icon: Percent },
      { to: "/setup/document-numbering", labelKey: "nav.documentNumbering", icon: Hash },
      { to: "/setup/cash-denominations", labelKey: "nav.cashDenominations", icon: Banknote },
      { to: "/opening-balances", labelKey: "nav.openingBalances", icon: Flag },
      { to: "/sales/print-settings", labelKey: "nav.invoicePrintSettings", icon: Printer },
    ]
  } as NavGroup,
  {
    labelKey: "nav.settings", icon: UserCog, items: [
      { to: "/settings/company", labelKey: "nav.companyInfo", icon: Building },
      { to: "/settings/members", labelKey: "nav.members", icon: Users },
      { to: "/settings/roles", labelKey: "nav.rolesPermissions", icon: ShieldCheck },
      { to: "/settings/ai", labelKey: "nav.aiKeys", icon: KeyRound },
      { to: "/settings/export", labelKey: "nav.dataExport", icon: Download },
      { to: "/profile", labelKey: "nav.accountDeletion", icon: Trash2 },
      { to: "/integrations", labelKey: "nav.integrations", icon: PlugZap },
      { to: "/billing", labelKey: "nav.billing", icon: CreditCard },
    ]
  } as NavGroup,
];

// Drop temporarily hidden pages from the navigation tree.
const visibleGroups: Entry[] = groups
  .map((e) => {
    if ("divider" in e) return e;
    if ("items" in e) return { ...e, items: e.items.filter((i) => !isHiddenRoute(i.to)) } as Entry;
    return isHiddenRoute((e as NavItem).to) ? null : e;
  })
  .filter((e): e is Entry => e !== null && (!("items" in (e as any)) || (e as NavGroup).items.length > 0));

const linkBase = "flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] leading-tight transition-colors";
const linkClass = ({ isActive }: { isActive: boolean }) =>
  `${linkBase} ${isActive ? "bg-sidebar-primary text-sidebar-primary-foreground font-medium shadow-sm" : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"}`;

const SoonBadge = () => {
  const { t } = useTranslation();
  return <span className="ms-auto text-[10px] rounded bg-muted text-muted-foreground px-1.5 py-0.5">{t("common.comingSoon")}</span>;
};

interface SidebarBodyProps {
  onNavigate?: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const SidebarBody = ({ onNavigate, collapsed, onToggleCollapse }: SidebarBodyProps) => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { confirm, markClean } = useDirtyNav();
  const isRtl = i18n.dir() === "rtl";

  const goHome = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const ok = await confirm();
    if (!ok) return;
    markClean();
    onNavigate?.();
    navigate("/dashboard");
  };

  const { data: perms } = usePermissions();

  const canShow = (to: string): boolean => {
    if (!perms || perms.isOwner || perms.isAdmin) return true;
    const key = getPageKeyForPath(to);
    if (!key) return true; // unrestricted route
    return (perms.levels[key] ?? "none") !== "none";
  };

  const findGroupForPath = (path: string): string | null => {
    let best: { key: string; len: number } | null = null;
    for (const g of visibleGroups) {
      if (!("items" in g)) continue;
      for (const it of g.items) {
        if (it.to === "/" ? path === "/" : path === it.to || path.startsWith(it.to + "/")) {
          if (!best || it.to.length > best.len) best = { key: g.labelKey, len: it.to.length };
        }
      }
    }
    return best?.key ?? null;
  };

  const [openGroup, setOpenGroup] = useState<string | null>(() => findGroupForPath(pathname) ?? "nav.transactions");

  useEffect(() => {
    const found = findGroupForPath(pathname);
    if (found) setOpenGroup(found);
  }, [pathname]);

  const itemCollapsedClasses = collapsed ? "justify-center px-2 gap-0" : "";

  const renderItem = (it: NavItem) => (
    <NavLink
      to={it.to}
      key={it.to}
      className={({ isActive }) => `${linkClass({ isActive })} ${itemCollapsedClasses}`}
      end={it.to === "/"}
      onClick={(e) => {
        if (it.soon) { e.preventDefault(); return; }
        onNavigate?.();
      }}
      title={collapsed ? t(it.labelKey) : undefined}
    >
      <it.icon className="h-4 w-4 shrink-0" />
      {!collapsed && (
        <>
          <span className="truncate">{t(it.labelKey)}</span>
          {it.soon && <SoonBadge />}
        </>
      )}
    </NavLink>
  );

  const collapseLabel = collapsed ? t("common.expandSidebar") : t("common.collapseSidebar");
  const CollapseIcon = collapsed
    ? (isRtl ? ChevronLeft : ChevronRight)
    : (isRtl ? ChevronRight : ChevronLeft);

  return (
    <>
      <div
        className={`flex border-b border-sidebar-border ${
          collapsed
            ? "flex-col items-center justify-center gap-1.5 px-2 h-20"
            : "flex-row items-center justify-between px-4 h-20"
        }`}
      >
        <a
          href="/dashboard"
          onClick={goHome}
          aria-label={tr("العودة إلى الصفحة الرئيسية", "Go to home page", "Aller à la page d'accueil", "ہوم پیج پر جائیں")}
          title={tr("العودة إلى الصفحة الرئيسية", "Go to home page", "Aller à la page d'accueil", "ہوم پیج پر جائیں")}
          className="rounded-sm outline-none transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-sidebar-ring"
        >
          {collapsed ? (
            <SidaMark className="h-8 w-8 select-none" />
          ) : (
            <div className="flex flex-col items-start justify-center gap-1.5">
              <SidaWordmark className="h-7 select-none" />
              <div className="text-[10px] leading-none tracking-[0.02em] text-sidebar-foreground/55 truncate">
                {t("app.tagline")}
              </div>
            </div>
          )}
        </a>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          aria-label={collapseLabel}
          title={collapseLabel}
          className="shrink-0 text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent"
        >
          <CollapseIcon className="h-4 w-4" />
        </Button>
      </div>

      <nav className={`flex-1 overflow-y-auto py-2 space-y-0.5 ${collapsed ? "px-1.5" : "px-2"}`}>
        {visibleGroups.map((g, i) => {
          if ("divider" in g) return <div key={`d-${i}`} className="my-2 border-t border-sidebar-border/60" />;
          if ("to" in g) return canShow(g.to) ? renderItem(g) : null;
          const visibleItems = g.items.filter((it) => canShow(it.to));
          if (visibleItems.length === 0) return null;
          const isOpen = openGroup === g.labelKey;
          const isParentActive = g.items.some((child) =>
            pathname === child.to || pathname.startsWith(child.to + "/")
          );
          return (
            <Collapsible key={i} open={isOpen} onOpenChange={(v) => setOpenGroup(v ? g.labelKey : null)}>
              <CollapsibleTrigger
                title={collapsed ? t(g.labelKey) : undefined}
                className={`${linkBase} ${itemCollapsedClasses} w-full ${
                  isParentActive
                    ? "bg-sidebar-primary/[0.05] text-sidebar-primary hover:bg-sidebar-primary/[0.08]"
                    : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                }`}
              >
                <g.icon className="h-4 w-4 shrink-0" />
                {!collapsed && (
                  <>
                    <span className={`truncate ${isParentActive ? "font-normal" : "font-medium"}`}>{t(g.labelKey)}</span>
                    <ChevronDown className={`ms-auto h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </>
                )}
              </CollapsibleTrigger>

              <CollapsibleContent className={`mt-0.5 space-y-0.5 ${collapsed ? "ps-0" : "ps-3"}`}>
                {visibleItems.map(renderItem)}
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </nav>
    </>
  );
};

const COLLAPSED_KEY = "sida_sidebar_collapsed";

export const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, String(collapsed));
    } catch {
      // ignore storage errors in restricted contexts
    }
  }, [collapsed]);

  return (
    <aside
      className={`hidden md:flex shrink-0 flex-col bg-sidebar text-sidebar-foreground border-e border-sidebar-border h-screen sticky top-0 transition-all duration-200 ${
        collapsed ? "w-16" : "w-60"
      }`}
    >
      <SidebarBody collapsed={collapsed} onToggleCollapse={() => setCollapsed((v) => !v)} />
    </aside>
  );
};
